import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface SearchResult {
  type: 'user' | 'request';
  data: any;
}

// Fonction utilitaire pour nettoyer les chaînes de caractères
export function cleanString(str: string): string {
  return str
    .normalize('NFD') // Décompose les caractères accentués
    .replace(/[\u0300-\u036f]/g, '') // Supprime les diacritiques
    .replace(/[^\w\s]/g, '') // Supprime les caractères spéciaux et émojis
    .toLowerCase()
    .trim();
}

export async function search(searchQuery: string, currentUser?: { uid: string }): Promise<SearchResult[]> {
  const results: SearchResult[] = [];
  const searchQueryLower = searchQuery.toLowerCase();
  const cleanedSearchQuery = cleanString(searchQuery);
  
  console.log('Recherche pour:', searchQueryLower);
  console.log('Recherche nettoyée:', cleanedSearchQuery);

  // Recherche des utilisateurs
  const usersRef = collection(db, 'users');
  const usersQuery = query(
    usersRef,
    where('usernameLower', '>=', searchQueryLower),
    where('usernameLower', '<=', searchQueryLower + '\uf8ff'),
    orderBy('usernameLower'),
    limit(5)
  );

  // Recherche des demandes
  const requestsRef = collection(db, '2dr_requests');
  const titleQuery = query(
    requestsRef,
    where('titleLower', '>=', cleanedSearchQuery),
    where('titleLower', '<=', cleanedSearchQuery + '\uf8ff'),
    orderBy('titleLower'),
    orderBy('createdAt', 'desc'),
    limit(5)
  );

  const detailsQuery = query(
    requestsRef,
    where('detailsLower', '>=', cleanedSearchQuery),
    where('detailsLower', '<=', cleanedSearchQuery + '\uf8ff'),
    orderBy('detailsLower'),
    orderBy('createdAt', 'desc'),
    limit(5)
  );

  try {
    // Exécuter les requêtes en parallèle
    const [usersSnapshot, titleSnapshot, detailsSnapshot] = await Promise.all([
      getDocs(usersQuery),
      getDocs(titleQuery),
      getDocs(detailsQuery)
    ]);

    console.log('Résultats utilisateurs:', usersSnapshot.size);
    console.log('Résultats titres:', titleSnapshot.size);
    console.log('Résultats détails:', detailsSnapshot.size);

    // Fonction utilitaire pour vérifier si une chaîne correspond à la recherche
    const matchesSearch = (text: string) => {
      const cleanedText = cleanString(text);
      return cleanedText.includes(cleanedSearchQuery) || 
             text.toLowerCase().includes(searchQueryLower);
    };

    // Ajouter les résultats des utilisateurs
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      if (matchesSearch(userData.username)) {
        results.push({
          type: 'user',
          data: {
            id: doc.id,
            username: userData.username,
            profilePicture: userData.profilePicture
          }
        });
      }
    });

    // Ajouter les résultats des demandes (titres)
    const seenRequestIds = new Set();
    titleSnapshot.forEach(doc => {
      const requestData = doc.data();
      
      // Appliquer les mêmes règles de filtrage que dans TwoDR
      const isVisible = (() => {
        // Si la demande n'est pas publique, ne l'afficher que pour son créateur
        if (!requestData.isPublic) return currentUser?.uid === requestData.userId;
        
        // Pour les demandes avec montant fixe
        if (requestData.hasFixedAmount) {
          const totalDonations = (requestData.donations || [])
            .reduce((sum: number, d: any) => sum + d.amount, 0);
          
          // Calculer le montant restant
          const remainingAmount = requestData.amount - totalDonations;
          
          // Ne pas afficher si:
          // 1. L'objectif est atteint OU
          // 2. Le montant restant est inférieur au montant minimum de donation
          if (totalDonations >= requestData.amount || 
              remainingAmount < (requestData.minDonationAmount || 100)) {
            return false;
          }
        }
        
        return true;
      })();

      if (isVisible && matchesSearch(requestData.title) && !seenRequestIds.has(doc.id)) {
        seenRequestIds.add(doc.id);
        console.log('Demande trouvée (titre):', requestData.title);
        
        // Formater les donations pour un affichage correct
        const formattedDonations = (requestData.donations || []).map((d: any) => ({
          ...d,
          date: d.date ? new Date(d.date) : new Date(),
          isAnonymous: !!d.isAnonymous
        }));
        
        // S'assurer que toutes les propriétés nécessaires sont présentes
        results.push({
          type: 'request',
          data: { 
            id: doc.id, 
            title: requestData.title || 'Sans titre',
            details: requestData.details || '',
            amount: requestData.amount || 0,
            city: requestData.city || '',
            country: requestData.country || '',
            images: requestData.images || [],
            audioUrl: requestData.audioUrl || null,
            isPublic: requestData.isPublic ?? true,
            link: requestData.link || '',
            likes: requestData.likes || 0,
            minDonationAmount: requestData.minDonationAmount || 100,
            userId: requestData.userId || 'unknown',
            creatorName: requestData.creatorName || 'Anonyme',
            creatorCity: requestData.city || 'Non spécifié',
            creatorCountry: requestData.country || 'Non spécifié',
            creatorProfilePicture: requestData.creatorProfilePicture,
            donations: formattedDonations,
            uniqueDonorsCount: formattedDonations.filter((d: any) => !d.isAnonymous).length,
            hasFixedAmount: requestData.hasFixedAmount || false,
            createdAt: requestData.createdAt ? new Date(requestData.createdAt) : new Date()
          }
        });
      }
    });

    // Ajouter les résultats des demandes (détails)
    detailsSnapshot.forEach(doc => {
      const requestData = doc.data();
      
      // Appliquer les mêmes règles de filtrage
      const isVisible = (() => {
        if (!requestData.isPublic) return currentUser?.uid === requestData.userId;
        
        if (requestData.hasFixedAmount) {
          const totalDonations = (requestData.donations || [])
            .reduce((sum: number, d: any) => sum + d.amount, 0);
          const remainingAmount = requestData.amount - totalDonations;
          
          if (totalDonations >= requestData.amount || 
              remainingAmount < (requestData.minDonationAmount || 100)) {
            return false;
          }
        }
        
        return true;
      })();

      if (isVisible && !seenRequestIds.has(doc.id)) {
        seenRequestIds.add(doc.id);
        console.log('Demande trouvée (détails):', requestData.title);
        
        // Formater les donations pour un affichage correct
        const formattedDonations = (requestData.donations || []).map((d: any) => ({
          ...d,
          date: d.date ? new Date(d.date) : new Date(),
          isAnonymous: !!d.isAnonymous
        }));
        
        // S'assurer que toutes les propriétés nécessaires sont présentes
        results.push({
          type: 'request',
          data: { 
            id: doc.id, 
            title: requestData.title || 'Sans titre',
            details: requestData.details || '',
            amount: requestData.amount || 0,
            city: requestData.city || '',
            country: requestData.country || '',
            images: requestData.images || [],
            audioUrl: requestData.audioUrl || null,
            isPublic: requestData.isPublic ?? true,
            link: requestData.link || '',
            likes: requestData.likes || 0,
            minDonationAmount: requestData.minDonationAmount || 100,
            userId: requestData.userId || 'unknown',
            creatorName: requestData.creatorName || 'Anonyme',
            creatorCity: requestData.city || 'Non spécifié',
            creatorCountry: requestData.country || 'Non spécifié',
            creatorProfilePicture: requestData.creatorProfilePicture,
            donations: formattedDonations,
            uniqueDonorsCount: formattedDonations.filter((d: any) => !d.isAnonymous).length,
            hasFixedAmount: requestData.hasFixedAmount || false,
            createdAt: requestData.createdAt ? new Date(requestData.createdAt) : new Date()
          }
        });
      }
    });

    console.log('Résultats totaux:', results.length);

  } catch (error) {
    console.error('Erreur lors de la recherche:', error);
  }

  return results;
}